import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import AddGlobalTaskModal from '../components/AddGlobalTaskModal';
import GlobalTaskDetailModal from '../components/GlobalTaskDetailModal';
import StatusDropdown from '../components/StatusDropdown';
import AssigneePicker from '../components/AssigneePicker';
import './GlobalTasks.css';

const GlobalTasks = ({ token }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  useEffect(() => {
    fetchGlobalTasks();
  }, []);

  const fetchGlobalTasks = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/global-tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTasks(data.tasks || []);
      }
    } catch (error) {
      console.error('Failed to fetch global tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = () => {
    fetchGlobalTasks();
  };

  const handleUpdateTask = () => {
    fetchGlobalTasks();
    setSelectedTask(null);
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/global-tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        fetchGlobalTasks();
      }
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleUpdateAssignee = async (taskId, assignee) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/global-tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assignee: assignee })
      });
      if (response.ok) {
        fetchGlobalTasks();
      }
    } catch (error) {
      console.error('Failed to update assignee:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/global-tasks/${taskId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchGlobalTasks();
      }
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  if (loading) return <div className="loading-spinner"></div>;

  return (
    <div className="global-tasks-page">
      <div className="global-tasks-header">
        <h1 className="global-tasks-title">Global Tasks</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowAddModal(true)}
          data-testid="create-global-task-button"
        >
          <FiPlus size={18} />
          <span>New Task</span>
        </button>
      </div>
      
      <AddGlobalTaskModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddTask}
        token={token}
      />
      
      <GlobalTaskDetailModal
        isOpen={!!selectedTask}
        onClose={() => setSelectedTask(null)}
        task={selectedTask}
        token={token}
        onUpdate={handleUpdateTask}
      />
      
      <div className="global-tasks-list">
        {tasks.map((task) => (
          <div key={task.id} className="global-task-item" data-testid="global-task-item">
            <div 
              className="global-task-content"
              onClick={() => setSelectedTask(task)}
              style={{ cursor: 'pointer', flex: 1 }}
            >
              <div className="global-task-title">{task.title}</div>
              {task.description && <div className="global-task-desc">{task.description}</div>}
              {task.dueDate && (
                <div className="global-task-due">Due: {new Date(task.dueDate).toLocaleDateString()}</div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ minWidth: '120px' }}>
                <AssigneePicker
                  assignees={task.assignee ? [task.assignee] : []}
                  onChange={(assignees) => handleUpdateAssignee(task.id, assignees[0] || null)}
                  token={token}
                />
              </div>
              <StatusDropdown
                value={task.status}
                onChange={(newStatus) => handleUpdateStatus(task.id, newStatus)}
              />
              <button 
                className="task-delete-btn"
                onClick={() => handleDeleteTask(task.id)}
                title="Delete task"
                data-testid="delete-task-button"
              >
                <FiTrash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GlobalTasks;